// useFrameworks — fetches /frameworks with the current filters, exposes refetch().
//
// V1 mirrors useTenants exactly: re-fetch on filter change, no SWR-style
// cache, no optimistic updates. The editorial library is small (dozens of
// frameworks max for the foreseeable future); pagination kicks in at
// Phase 1 scale if it grows.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListFrameworksQuery, ListFrameworksResponse } from '@gc-coach/shared';
import { listFrameworks } from '../api/endpoints.js';

export interface UseFrameworksResult {
  data: ListFrameworksResponse | null;
  isLoading: boolean;
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useFrameworks(query: ListFrameworksQuery): UseFrameworksResult {
  const [data, setData] = useState<ListFrameworksResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const key = useMemo(
    () => `${query.status ?? ''}|${query.domain ?? ''}`,
    [query.status, query.domain],
  );

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    const result = await listFrameworks(query);
    if (result.ok) {
      setData(result.data);
      setErrorCode(null);
    } else {
      setErrorCode(result.code);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  return { data, isLoading, errorCode, refetch: fetchNow };
}
